import type { Drawer } from '@mui/material';
import { ToggleButton } from '@mui/material';
import { INVOICES_OVERVIEWS_ADDITIONAL_FILTERS } from '@weft/api-definition';
import { INVOICE_BILLING_STATUSES, INVOICE_REVIEW_STATUSES } from '@weft/types';
import type { InvoiceBillingStatus } from '@weft/types';
import type { InvoiceReviewStatus } from '@weft/types';
import { values } from '@weft/utils';
import React from 'react';
import type { ComponentProps } from 'react';
import { FilterDrawer } from '../../../../components/design/filter-drawer/FilterDrawer';
import { usePageParam } from '../../../../hooks/search-params/use-page-param';
import { useSelectedAdditionalFilters } from '../invoices-page-hooks/use-additional-filters';
import { useSelectedBillingStatusesParam } from '../invoices-page-hooks/use-selected-billing-statuses-param';
import { useSelectedWorkflowsParam } from '../invoices-page-hooks/use-selected-workflows-param';
import { getBillingStatusLabel } from './InvoiceBillingStatusLabel';
import { getReviewStatusLabel } from './InvoiceOverviewTable/invoice-overview-table-consts/invoice-columns';

export const InvoicesFilterDrawer: React.FC<ComponentProps<typeof Drawer>> = (props) => {
  const [selectedBillingStatuses, setSelectedBillingStatuses] = useSelectedBillingStatusesParam();
  const [selectedWorkflows, setSelectedWorkflows] = useSelectedWorkflowsParam();
  const [
    selectedAdditionalFilters,
    setSelectedAdditionalFilters,
  ] = useSelectedAdditionalFilters();
  const [_page, setPage] = usePageParam();

  const handleBillingStatusFiltersChange = (billingStatus: InvoiceBillingStatus) => {
    if (selectedBillingStatuses?.includes(billingStatus)) {
      setSelectedBillingStatuses(selectedBillingStatuses.filter((status) => status !== billingStatus));
    } else {
      setSelectedBillingStatuses([...(selectedBillingStatuses ?? []), billingStatus]);
    }

    setPage(0);
  };

  const handleWorkflowFiltersChange = (workflow: InvoiceReviewStatus) => {
    if (selectedWorkflows?.includes(workflow)) {
      setSelectedWorkflows(selectedWorkflows.filter((status) => status !== workflow));
    } else {
      setSelectedWorkflows([...(selectedWorkflows ?? []), workflow]);
    }

    setPage(0);
  };

  return (
    <FilterDrawer {...props}>
      <FilterDrawer.Section label="Status">
        <div className="flex flex-wrap gap-2">
          {values(INVOICE_BILLING_STATUSES).map(invoiceBillingStatus => (
            <ToggleButton
              key={crypto.randomUUID()}
              value={invoiceBillingStatus}
              className="border-style text-style rounded px-4 py-2 text-base font-normal"
              sx={{
                textTransform: 'none',
              }}
              selected={selectedBillingStatuses?.includes(invoiceBillingStatus)}
              onChange={() => handleBillingStatusFiltersChange(invoiceBillingStatus)}
            >
              {getBillingStatusLabel(invoiceBillingStatus)}
            </ToggleButton>
          ))}
        </div>
      </FilterDrawer.Section>

      <FilterDrawer.Section label="Workflow">
        {values(INVOICE_REVIEW_STATUSES).map(invoicesReviewStatus => (
          <ToggleButton
            key={crypto.randomUUID()}
            value={invoicesReviewStatus}
            className="border-style text-style rounded px-4 py-2 text-base font-normal"
            sx={{
              textTransform: 'none',
            }}
            selected={selectedWorkflows?.includes(invoicesReviewStatus)}
            onChange={() => handleWorkflowFiltersChange(invoicesReviewStatus)}
          >
            {getReviewStatusLabel(invoicesReviewStatus)}
          </ToggleButton>
        ))}
      </FilterDrawer.Section>

      <FilterDrawer.Section label="Additional Filters">
        <ToggleButton
          value={1}
          selected={selectedAdditionalFilters?.includes(INVOICES_OVERVIEWS_ADDITIONAL_FILTERS.Overage)}
          className="border-style text-style rounded px-4 py-2 text-base font-normal"
          sx={{
            textTransform: 'none',
          }}
          onChange={() => {
            if (selectedAdditionalFilters?.includes(INVOICES_OVERVIEWS_ADDITIONAL_FILTERS.Overage)) {
              setSelectedAdditionalFilters(selectedAdditionalFilters.filter(filter => filter !== INVOICES_OVERVIEWS_ADDITIONAL_FILTERS.Overage));
            } else {
              setSelectedAdditionalFilters([...(selectedAdditionalFilters ?? []), INVOICES_OVERVIEWS_ADDITIONAL_FILTERS.Overage]);
            }
          }}
        >
          Overage
        </ToggleButton>
      </FilterDrawer.Section>
    </FilterDrawer>
  );
};